import * as Sentry from '@sentry/nextjs'
import { useSearchParams } from 'next/navigation'
import { useEffect } from 'react'

import { localeToCountryCode } from '@/types/locales'

import { useLead } from '@/context/leadContext'

import { getUserType } from '@/components/shared/stripe-checkout-form/helpers'
import { readPaymentReturn } from '@/components/shared/stripe-intent-checkout/paymentReturn'

import { getLeadId } from '@/lib/attribution/leadId'
import { useTrackedRouter } from '@/lib/attribution/useTrackedRouter'
import { fetchPurchase } from '@/lib/checkout/fetchPurchase'
import { forwardMarketingPurchase } from '@/lib/checkout/forwardMarketingPurchase'
import { captureGtmServerPreviewToken } from '@/lib/checkout/gtmServerPreview'
import type { IntentPrice } from '@/lib/checkout/intent/prices'
import { extractPurchases, resolvePurchaseCountryCode } from '@/lib/checkout/purchaseSessionParams'
import { recordMergedAttributionPurchase, trackSessionPurchaseEvents } from '@/lib/checkout/purchaseTracking'
import { IntentPollError } from '@/lib/checkout/sessionDetailsCache'
import { hasTrackedPurchase } from '@/lib/checkout/trackedPurchaseSessions'
import { useAuthInfo } from '@/lib/hooks/useAuthInfo'
import { purchaseProductOf, recordPurchase } from '@/lib/sentry/paymentTiming'

/**
 * Reports a purchase as soon as a paid Stripe checkout session is seen on the URL. Every
 * post-payment URL carries the session id (see `purchaseSessionParams`), so mounting this once at
 * the app root catches whichever page the customer reaches first. An intent checkout's lines are named from the
 * build-time prices of that page, so it is tracked only on a page that has them (its confirmation route).
 */
export const PurchaseTracker = ({ intentPrices }: { intentPrices?: IntentPrice[] }) => {
  const searchParams = useSearchParams()
  const router = useTrackedRouter()
  const { authenticatedUser } = useAuthInfo()
  const { leadData } = useLead()

  const guestEmail = leadData?.email ?? undefined
  const locale = typeof router.query.locale === 'string' ? router.query.locale : undefined

  useEffect(() => {
    // Before the guards: the tester lands with ?gtm_preview= long before any purchase exists.
    captureGtmServerPreviewToken(searchParams)

    if (!authenticatedUser) return

    // Every purchase on the URL, not only those still owed a dataLayer event: attribution merges the
    // whole basket. Session reads are cached, so re-reading one is free.
    // A failed Klarna return lands on the checkout page with the reference it did not pay.
    const isFailedReturn = readPaymentReturn(Object.fromEntries(searchParams ?? []))?.status === 'failed'
    const purchases = extractPurchases(searchParams).filter((p) => !(isFailedReturn && p.kind === 'intent'))
    // A later page (e.g. a cross-sell offer) would rewrite the attribution row with unnamed lines.
    if (purchases.some((p) => p.kind === 'intent') && !intentPrices) return
    if (purchases.length === 0) return
    if (purchases.every((p) => hasTrackedPurchase(p.id)) && !getLeadId(searchParams)) return

    const countryCode = resolvePurchaseCountryCode(searchParams, locale ? localeToCountryCode(locale) : undefined)
    if (!countryCode) return

    const userType = getUserType(authenticatedUser)
    // An intent reference is polled rather than read once: nothing re-renders this component when it settles.
    const context = { user: authenticatedUser, countryCode, guestEmail, intentPrices: intentPrices ?? [] }

    const fetched = purchases.map((purchase) => {
      const isIntent = purchase.kind === 'intent'
      const sessionId = purchase.id
      // Read before the dataLayer push below marks it, so a purchase already reported on an earlier
      // page is not sent to Sentry again; recordPurchase dedupes within this page.
      const isNewPurchase = !hasTrackedPurchase(sessionId)
      return fetchPurchase(purchase, context)
        .then((session) => {
          if (isNewPurchase) {
            const product = purchaseProductOf((session?.line_items ?? []).map((item) => item.price?.product_type))
            recordPurchase(
              sessionId,
              session?.status === 'COMPLETE' ? 'complete' : 'open',
              product,
              isIntent ? 'intent' : 'session',
            )
          }
          // An OPEN session can still be paid; it is picked up on the next page carrying it.
          if (!session || session.status !== 'COMPLETE') return null
          // Both dedupe internally on their own channel, so re-reading a session for attribution
          // cannot double-report either.
          trackSessionPurchaseEvents({
            session,
            countryCode,
            userType,
            userEmail: isIntent ? session.customer.email || undefined : (authenticatedUser.email ?? undefined),
            checkoutPath: isIntent ? 'intent' : 'session',
          })
          // Server-side copy of the same purchase, gated on marketing consent. Independent of the
          // dataLayer push above: it waits for a consent answer, so it must not hold that up.
          void forwardMarketingPurchase(session, countryCode)
          return session
        })
        .catch((error: unknown) => {
          // A poll that gave up is reported once, by the confirmation page.
          if (!(error instanceof IntentPollError)) {
            Sentry.captureException(error, {
              extra: { firebaseUID: authenticatedUser.uid, sessionId, countryCode, tracker: 'PurchaseTracker' },
            })
          }
          return null
        })
    })

    // Attribution's copy, once the whole basket is known.
    void Promise.all(fetched).then((sessions) => {
      recordMergedAttributionPurchase(
        sessions.filter((session) => session !== null),
        getLeadId(searchParams),
      )
    })
  }, [authenticatedUser, searchParams, locale, guestEmail, intentPrices])

  return null
}
